import { Box, Tooltip, Typography } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
import { CHART_COLORS } from 'lib/functions/const/colors';
import { BarChartData } from 'lib/types/shared';
import React, { useMemo } from 'react';

interface BarChartProps {
  data: BarChartData;
  colors?: string[];
  height?: number;
  showLegend?: boolean;
  formatYAxis?: (value: number) => string;
  yAxisFormat?: 'K' | 'M' | 'MM' | 'auto';
  direction?: 'horizontal' | 'vertical';
  xAxisLabel?: string;
  yAxisLabel?: string;
  stacked?: boolean;
  barGapRatio?: number;
  categoryGapRatio?: number;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  colors = Object.values(CHART_COLORS),
  height = 300,
  showLegend = true,
  formatYAxis,
  yAxisFormat = 'auto',
  direction = 'vertical',
  xAxisLabel,
  yAxisLabel,
  stacked = false,
  barGapRatio,
  categoryGapRatio,
}) => {
  const getFormatFunction = () => {
    if (formatYAxis) return formatYAxis;

    switch (yAxisFormat) {
      case 'K':
        return (value: number) => {
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'M':
        return (value: number) => {
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'MM':
        return (value: number) => {
          if (value >= 1000000000) {
            const mmValue = value / 1000000000;
            return `${mmValue.toFixed(1)}MM`;
          }
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
      case 'auto':
      default:
        return (value: number) => {
          if (value >= 1000000000) {
            const mmValue = value / 1000000000;
            return `${mmValue.toFixed(1)}MM`;
          }
          if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
          if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
          return value.toString();
        };
    }
  };

  const finalFormatYAxis = getFormatFunction();
  const dataKeys = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter((key) => key !== 'name');
  }, [data]);

  const series = useMemo(() => {
    return dataKeys.map((key, index) => ({
      id: key,
      label: key,
      data: data.map((item) => item[key] as number),
      color: colors[index % colors.length],
      valueFormatter: (v: number | null) =>
        v === null ? '' : finalFormatYAxis(v),
      ...(stacked && { stack: 'A' }),
    }));
  }, [data, dataKeys, colors, finalFormatYAxis, stacked]);

  // Determinar si una serie es la última del stack
  const isLastSeries = (seriesId: string) => {
    if (!stacked || dataKeys.length === 0) return false;
    return seriesId === dataKeys[dataKeys.length - 1];
  };

  const chartHeight = height || 300;

  const truncateLabel = (text: string) => {
    return text.length > 15 ? `${text.substring(0, 15)}...` : text;
  };
  const dataset = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((item, index) => ({
      id: index,
      name: item.name,
      ...Object.fromEntries(dataKeys.map((key) => [key, item[key] as number])),
    }));
  }, [data, dataKeys]);

  return (
    <Box sx={{ width: '100%' }}>
      {showLegend && dataKeys.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            marginBottom: '8px',
          }}
        >
          {dataKeys.map((key, index) => (
            <Tooltip
              key={key}
              title={`${key}: ${finalFormatYAxis(
                data.reduce(
                  (sum, item) => sum + ((item[key] as number) || 0),
                  0,
                ),
              )}`}
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: colors[index % colors.length],
                    borderRadius: '2px',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666666',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {key}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      <MuiBarChart
        height={chartHeight}
        series={series}
        xAxis={
          direction === 'horizontal'
            ? [
                {
                  scaleType: 'linear',
                  label: xAxisLabel,
                  tickLabelStyle: {
                    fill: '#B0B0B0',
                    fontSize: 12,
                  },
                  valueFormatter: finalFormatYAxis,
                  labelStyle: { fill: '#B0B0B0' },
                },
              ]
            : [
                {
                  dataKey: 'name',
                  scaleType: 'band',
                  label: xAxisLabel,
                  ...(barGapRatio !== undefined && { barGapRatio }),
                  ...(categoryGapRatio !== undefined && { categoryGapRatio }),
                  valueFormatter: (name: string, context) =>
                    context.location === 'tick'
                      ? truncateLabel(String(name))
                      : String(name),
                  tickLabelStyle: {
                    fill: '#B0B0B0',
                    fontSize: 12,
                    textAnchor: 'end',
                  },
                },
              ]
        }
        yAxis={
          direction === 'horizontal'
            ? [
                {
                  dataKey: 'name',
                  scaleType: 'band',
                  label: yAxisLabel,
                  ...(barGapRatio !== undefined && { barGapRatio }),
                  ...(categoryGapRatio !== undefined && { categoryGapRatio }),
                  valueFormatter: (name: string, context) =>
                    context.location === 'tick'
                      ? truncateLabel(String(name))
                      : String(name),
                  tickLabelStyle: {
                    fill: '#B0B0B0',
                    fontSize: 12,
                    textAnchor: 'end',
                  },
                },
              ]
            : [
                {
                  scaleType: 'linear',
                  label: yAxisLabel,
                  tickLabelStyle: {
                    fill: '#B0B0B0',
                    fontSize: 12,
                  },
                  valueFormatter: finalFormatYAxis,
                  labelStyle: {
                    fill: '#B0B0B0',
                  },
                },
              ]
        }
        dataset={dataset}
        layout={direction}
        margin={{
          top: 10,
          right: 10,
          bottom: 40,
          left: direction === 'horizontal' ? 120 : 60,
        }}
        grid={{
          horizontal: true,
          vertical: false,
        }}
        borderRadius={4}
        slotProps={{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          bar: (ownerState: any) => {
            const seriesId = ownerState?.seriesId as string;
            const isLast = isLastSeries(seriesId);

            if (stacked && !isLast) {
              return {
                clipPath:
                  direction === 'horizontal'
                    ? 'inset(0px round 0px 0px 0px 0px)'
                    : 'inset(0px round 0px 0px 0px 0px)',
              };
            }
            return {
              clipPath:
                direction === 'horizontal'
                  ? 'inset(0px round 0px 4px 4px 0px)'
                  : 'inset(0px round 4px 4px 0px 0px)',
            };
          },
        }}
      />
    </Box>
  );
};

export default BarChart;
